/**
 * CDN-relative path of the image used wherever an event has no cover image of
 * its own. Only applies when the CDN `config.json` is unreachable — SSR,
 * localhost, or a failed fetch — since `coverFallback` overrides it there.
 */
export const DEFAULT_COVER_FALLBACK_PATH = '/assets/img/placeholders/cover.jpg';

export interface RuntimeAppConfig {
  ttl: number;
  googleClientId: string;
  seatsioWorkspaceKey: string;
  cloudflareCaptchaSiteKey: string;
  fanPortalUrl: string;
  checkoutUrl: string;
  controlHubUrl: string;
  controlHubDesktopUrl: string;
  statusUrl: string;
  conektaPublicKey: string;
  fintocPublicKey: string;
  digitalfemsaPublicKey: string;
  whatsappNumber: string;
  showDiscountPreview?: boolean;
  phoneLogin?: boolean;
  /**
   * Nombre viejo de `phoneLogin`. Sigue siendo la llave que trae el `config.json`
   * publicado en el CDN, que se edita fuera del repo. Leerlo directamente deja
   * fuera el alias: pasa por `normalizePublishedConfig`.
   *
   * @deprecated Se elimina al republicar el `config.json` con `phoneLogin`.
   */
  whatsappLogin?: boolean;
  appleLogin?: boolean;
  appleServicesId: string;
  appleRedirectUri: string;
  mentaReturnToDomain?: string;
  // Optional because a config.json published before this field existed simply
  // omits it — the JSON is cast, not validated, so consumers must tolerate it
  // being absent (see DEFAULT_COVER_FALLBACK_PATH).
  coverFallback?: string;
}

/**
 * El `config.json` del CDN se publica fuera del repo, asi que un rename de campo
 * aqui no lo alcanza: hasta que se republique con la llave nueva, el payload
 * sigue trayendo la vieja y el consumidor leeria `undefined`.
 */
function normalizePublishedConfig(raw: RuntimeAppConfig): RuntimeAppConfig {
  return { ...raw, phoneLogin: raw.phoneLogin ?? raw.whatsappLogin };
}

async function fetchWithRetry(
  url: string,
  signal?: AbortSignal,
  maxRetries?: number,
): Promise<RuntimeAppConfig> {
  const INITIAL_DELAY = 1_000;
  const MAX_DELAY = 30_000;

  let delay = INITIAL_DELAY;
  let attempts = 0;

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      const response = await fetch(url, { signal, cache: 'no-cache' });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return normalizePublishedConfig(
        (await response.json()) as RuntimeAppConfig,
      );
    } catch (err) {
      if (signal?.aborted) {
        throw err;
      }

      attempts++;

      if (maxRetries !== undefined && attempts >= maxRetries) {
        throw new Error(
          `Failed to fetch runtime config after ${attempts} attempts`,
        );
      }

      await new Promise<void>((resolve) => setTimeout(resolve, delay));
      delay = Math.min(delay * 2, MAX_DELAY);
    }
  }
}

export async function loadRuntimeConfig(
  cdnUrl: string,
): Promise<RuntimeAppConfig> {
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    return buildServerRuntimeConfig();
  }

  try {
    return await fetchWithRetry(`${cdnUrl}/config.json`, undefined, 5);
  } catch {
    return buildServerRuntimeConfig();
  }
}

export function buildServerRuntimeConfig(): RuntimeAppConfig {
  return {
    ttl: 600_000,
    googleClientId: process.env['STX_GOOGLE_CLIENT_ID'] ?? '',
    seatsioWorkspaceKey: process.env['STX_SEATSIO_WORKSPACE_KEY'] ?? '',
    cloudflareCaptchaSiteKey:
      process.env['STX_CLOUDFLARE_CAPTCHA_SITE_KEY'] ?? '',
    fanPortalUrl: process.env['STX_FAN_PORTAL_URL'] ?? '',
    checkoutUrl: process.env['STX_CHECKOUT_URL'] ?? '',
    controlHubUrl: process.env['STX_CONTROL_HUB_URL'] ?? '',
    controlHubDesktopUrl: process.env['STX_CONTROL_HUB_DESKTOP_URL'] ?? '',
    statusUrl: process.env['STX_STATUS_URL'] ?? '',
    conektaPublicKey: process.env['STX_CONEKTA_PUBLIC_KEY'] ?? '',
    fintocPublicKey: process.env['STX_FINTOC_PUBLIC_KEY'] ?? '',
    digitalfemsaPublicKey: process.env['STX_DIGITALFEMSA_PUBLIC_KEY'] ?? '',
    whatsappNumber: process.env['STX_WHATSAPP_NUMBER'] ?? '',
    showDiscountPreview: process.env['STX_SHOW_DISCOUNT_PREVIEW'] === 'true',
    // STX_WHATSAPP_LOGIN is the transitional alias: the variable is set per
    // environment in DigitalOcean, not in the repo, so dropping it here would
    // silently turn the phone login button off in production.
    phoneLogin:
      process.env['STX_PHONE_LOGIN'] === 'true' ||
      process.env['STX_WHATSAPP_LOGIN'] === 'true',
    appleLogin: process.env['STX_APPLE_LOGIN'] === 'true',
    appleServicesId: process.env['STX_APPLE_SERVICES_ID'] ?? '',
    appleRedirectUri: process.env['STX_APPLE_REDIRECT_URI'] ?? '',
    mentaReturnToDomain:
      process.env['STX_MENTA_RETURN_TO_DOMAIN'] ?? 'mentatickets.com',
    coverFallback:
      process.env['STX_COVER_FALLBACK'] ?? DEFAULT_COVER_FALLBACK_PATH,
  };
}
